'use client'

import { useMergedRef, useResizeObserver } from '@mantine/hooks'
import * as Sentry from '@sentry/react'
import { ShowMore } from 'app/_components/Editor/Toolbar/ShowMore'
import type { CSSProperties } from 'react'
import { createContext, use, useMemo, useState } from 'react'
import type { BaseOperation } from 'slate'
import { type Descendant } from 'slate'
import { Slate } from 'slate-react'
import { twMerge } from 'tailwind-merge'
import { AutoSaveStatus, type Status } from './AutoSaveStatus'
import { Editable } from './Editable'
import { EditorContentContext } from './EditorContentContext'
import EditorError from './Error'
import { type Extension, useEditorWithExtensions } from './extensions'
import { Header } from './Header'
import { KeyEventProvider } from './KeyEventContext'
import styles from './styles.module.css'
import type { ToolbarConfig } from './Toolbar'
import { Toolbar } from './Toolbar'

export type EditorVariants =
  | 'default'
  | 'journal'
  | 'comment'
  | 'post'
  | 'chapter-meta'
  | 'mini'

export type EditorProps = {
  value?: Descendant[]
  extensions?: Extension[]
  onChange?: (value: Descendant[], operations: BaseOperation[]) => void
  readOnly?: boolean
  className?: string
  textboxClassName?: string
  toolbar?: ToolbarConfig
  autoSaveStatus?: Status
  placeholder?: string
  variant?: EditorVariants
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  autoFocus?: boolean
  renderPlaceholderInReadOnly?: boolean
  id?: string
  'aria-invalid'?: boolean
  'aria-labelledby'?: string
  'aria-errormessage'?: string
}

const defaultValue: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
]

type HeaderContextType = [boolean, () => void]

export const HeaderContext = createContext<HeaderContextType>([false, () => {}])

export function Editor({
  value = defaultValue,
  extensions = [],
  onChange,
  readOnly,
  className,
  textboxClassName,
  toolbar,
  autoSaveStatus,
  placeholder,
  variant = 'default',
  onKeyDown,
  autoFocus = true,
  renderPlaceholderInReadOnly = false,
  id,
  ...passThroughProps
}: EditorProps) {
  const editor = useEditorWithExtensions(extensions)
  const [, setEditorContent] = use(EditorContentContext)

  const showToolbar = !(readOnly || variant === 'comment')

  const [toolbarEl, setToolbarEl] = useState<HTMLDivElement | null>(null)
  const [resizeObserverRef, { height: toolbarFullHeight }] =
    useResizeObserver<HTMLDivElement>()
  const toolbarRef = useMergedRef(setToolbarEl, resizeObserverRef)

  const [toolbarExtended, setToolbarExtended] = useState(false)
  const contextValue = useMemo<HeaderContextType>(
    () => [toolbarExtended, () => setToolbarExtended((extended) => !extended)],
    [toolbarExtended],
  )

  return (
    <Sentry.ErrorBoundary fallback={<EditorError />}>
      <Slate
        editor={editor}
        initialValue={value}
        onChange={
          readOnly
            ? undefined
            : (value) => {
                onChange?.(value, editor.operations)
                setEditorContent(value)
              }
        }
      >
        <div
          className={twMerge(styles.editor, className)}
          style={
            toolbarExtended
              ? ({
                  '--header-height': `${toolbarFullHeight}px`,
                } as CSSProperties)
              : undefined
          }
        >
          <HeaderContext.Provider value={contextValue}>
            {showToolbar && (
              <Header variant={variant}>
                {toolbar && <Toolbar ref={toolbarRef} config={toolbar} />}
                {autoSaveStatus && <AutoSaveStatus status={autoSaveStatus} />}
                <ShowMore
                  toolbar={toolbarEl}
                  toolbarHeight={toolbarFullHeight}
                />
              </Header>
            )}
            <KeyEventProvider>
              <Editable
                id={id}
                autoFocus={autoFocus}
                variant={variant}
                placeholder={placeholder}
                readOnly={readOnly}
                onKeyDown={onKeyDown}
                className={twMerge(
                  textboxClassName,
                  autoSaveStatus === 'error' &&
                    'outline-support-error! bg-support-error/10! outline-3',
                )}
                renderPlaceholderInReadOnly={renderPlaceholderInReadOnly}
                {...passThroughProps}
              />
            </KeyEventProvider>
          </HeaderContext.Provider>
        </div>
      </Slate>
    </Sentry.ErrorBoundary>
  )
}
